/**
 * `/app` — Dashboard.
 *
 * Composition: KPI strip (4 cards) on top, then a 12-col grid:
 *   left (8 cols)  — equity curve + open positions table
 *   right (4 cols) — live signals feed (long), market state, risk panel
 *
 * Each card subscribes to its own query (see `useDashboardApi`) so a
 * single endpoint failure leaves the rest intact.
 */
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/auth'

import { ActiveEventsBanner } from '@/components/dashboard/ActiveEventsBanner'
import { KpiCard } from '@/components/dashboard/KpiCard'
import { EquityCurve } from '@/components/dashboard/EquityCurve'
import { OpenPositions } from '@/components/dashboard/OpenPositions'
import { SignalsFeed } from '@/components/dashboard/SignalsFeed'
import { MarketState } from '@/components/dashboard/MarketState'
import { RiskPanel } from '@/components/dashboard/RiskPanel'
import { BybitAccountCard } from '@/components/dashboard/BybitAccountCard'

import {
  useDashboardSummary, useEquityCurve, useMarketState, useOpenPositions,
  useRecentSignals, useRiskUsage,
} from '@/hooks/useDashboardApi'
import { formatPct, formatUsd } from '@/lib/format'

const EQUITY_DAYS = 7

export default function Dashboard() {
  const { t } = useTranslation('app')
  const userEmail = useAuthStore((s) => s.user?.email)

  const summary = useDashboardSummary()
  const equity  = useEquityCurve(EQUITY_DAYS)
  const open    = useOpenPositions()
  const signals = useRecentSignals(40)
  const market  = useMarketState()
  const risk    = useRiskUsage()

  return (
    <div className="p-4 sm:p-6 max-w-[1400px]">
      {/* Page header */}
      <div className="mb-6">
        <div className="text-xs text-textFaint uppercase tracking-wider mb-1">
          {userEmail}
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {t('dashboard.title')}
        </h1>
        <div className="mt-1 text-sm text-textDim">
          {t('dashboard.subtitle')}
        </div>
      </div>

      {/* PULSE-42: active period events (tests / incidents / etc) */}
      <ActiveEventsBanner />

      {/* KPI strip — single line on md+, 2x2 on small */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-line mb-6">
        <KpiCard
          label={t('dashboard.kpi.pnl_today')}
          value={
            summary.data
              ? formatUsd(summary.data.realized_pnl_today, { sign: true })
              : '—'
          }
          sub={
            summary.data
              ? t('dashboard.kpi.pnl_today_sub', { count: summary.data.trades_today })
              : undefined
          }
          tone={
            summary.data
              ? summary.data.realized_pnl_today > 0 ? 'pulse'
              : summary.data.realized_pnl_today < 0 ? 'rose'
              : 'neutral'
              : 'neutral'
          }
          loading={summary.isLoading}
        />
        <KpiCard
          label={t('dashboard.kpi.unrealized')}
          value={
            summary.data
              ? formatUsd(summary.data.unrealized_pnl, { sign: true })
              : '—'
          }
          sub={
            summary.data
              ? t(
                  summary.data.open_positions === 1
                    ? 'dashboard.kpi.unrealized_sub_one'
                    : 'dashboard.kpi.unrealized_sub',
                  { count: summary.data.open_positions },
                )
              : undefined
          }
          tone={
            summary.data
              ? summary.data.unrealized_pnl > 0 ? 'pulse'
              : summary.data.unrealized_pnl < 0 ? 'rose'
              : 'neutral'
              : 'neutral'
          }
          loading={summary.isLoading}
        />
        <KpiCard
          label={t('dashboard.kpi.win_rate_7d')}
          value={
            summary.data?.win_rate_7d != null
              ? `${summary.data.win_rate_7d.toFixed(0)}%`
              : '—'
          }
          sub={
            summary.data
              ? summary.data.trades_7d > 0
                ? t('dashboard.kpi.win_rate_sub', { trades: summary.data.trades_7d })
                : t('dashboard.kpi.win_rate_empty')
              : undefined
          }
          loading={summary.isLoading}
        />
        <KpiCard
          label={t('dashboard.kpi.signals_1h')}
          value={summary.data?.signals_1h ?? '—'}
          sub={
            summary.data
              ? summary.data.signals_1h_matched > 0
                ? t('dashboard.kpi.signals_sub',
                     { matched: summary.data.signals_1h_matched })
                : t('dashboard.kpi.signals_sub_zero')
              : undefined
          }
          tone={
            summary.data && summary.data.signals_1h_matched > 0 ? 'sky' : 'neutral'
          }
          loading={summary.isLoading}
        />
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-12 gap-px bg-line">
        {/* Left column — chart + open positions */}
        <div className="col-span-12 lg:col-span-8 bg-line">
          <EquityCurve
            data={equity.data?.points}
            days={EQUITY_DAYS}
            loading={equity.isLoading}
          />
          <div className="h-px bg-line" />
          <OpenPositions
            items={open.data?.items}
            loading={open.isLoading}
          />
        </div>

        {/* Right column — signals + market + risk */}
        <div className="col-span-12 lg:col-span-4 bg-line flex flex-col">
          <div className="h-[400px]">
            <SignalsFeed items={signals.data?.items} loading={signals.isLoading} />
          </div>
          <div className="h-px bg-line" />
          <MarketState data={market.data} loading={market.isLoading} />
          <div className="h-px bg-line" />
          <RiskPanel data={risk.data} loading={risk.isLoading} />
          <div className="h-px bg-line" />
          <BybitAccountCard />
        </div>
      </div>
    </div>
  )
}
